import {useContext, useEffect, useMemo} from "react";
import {useParams, useSearchParams} from "react-router-dom";
import {observer} from "mobx-react-lite";
import styled from "styled-components";
import {ApiContext} from "../index";
import {TerminiStore} from "./termini_store";
import {TerminiSlot} from "./termini_slot";
import {TailSpin} from "react-loader-spinner";
import {StyledProps} from "../theme";
import {calendar} from "../proto/compiled";
import {zfill} from "../utils";
import {CvvButton} from "../components/buttons";
import {CvvRoutes} from "../strings";

const PageContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 16px;
    padding: 16px;
    background: transparent;
    box-sizing: border-box;
    align-items: start;
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    min-height: 200px;
`;

const SlotWrapper = styled.div`
`;

const EmptyMessage = styled.div`
    text-align: center;
    color: ${({theme}: StyledProps) => theme.content};
    font-size: 16px;
    padding: 40px;
`;

const EmptyStateWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: calc(100vh - 32px);
    width: 100%;
    padding: 16px;
    box-sizing: border-box;
`;

const EmptyContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    max-width: 420px;
    padding: 48px 32px;
    background: white;
    box-shadow: 0 0.25em 0.5em rgba(0, 45, 98, .15);
`;

const EmptyIconContainer = styled.div`
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: ${({theme}: StyledProps) => theme.shade};
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
`;

const CalendarIcon = () => (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="4" width="18" height="18" rx="2" stroke="#002D62" strokeWidth="1.5"/>
        <path d="M3 9H21" stroke="#002D62" strokeWidth="1.5"/>
        <path d="M8 2V5" stroke="#002D62" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M16 2V5" stroke="#002D62" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="12" cy="15" r="1.5" fill="#002D62"/>
    </svg>
);

const EmptyTitle = styled.div`
    color: ${({theme}: StyledProps) => theme.content};
    font-size: 20px;
    font-weight: 700;
    margin-bottom: 16px;
    line-height: 1.3;
`;

const EmptyText = styled.div`
    color: ${({theme}: StyledProps) => theme.content};
    font-size: 15px;
    line-height: 1.6;
    margin-bottom: 8px;
`;

const ContactInfo = styled.div`
    color: ${({theme}: StyledProps) => theme.contentShade};
    font-size: 14px;
    line-height: 1.5;
    margin-bottom: 28px;

    a {
        color: ${({theme}: StyledProps) => theme.content};
        text-decoration: none;
        font-weight: 600;

        &:hover {
            text-decoration: underline;
        }
    }
`;

const SignUpButtonLink = styled(CvvButton).attrs({as: 'a'})`
    text-decoration: none;
    justify-content: center;
    width: auto;
    padding: 0 40px;
`;

export const TerminiPage = observer(() => {
    const {tip, tecaj} = useParams<{ tip: string; tecaj: string }>();
    const [searchParams] = useSearchParams();
    const api = useContext(ApiContext);

    const limit = parseInt(searchParams.get('n') ?? '9', 10);

    const store = useMemo(() => {
        if (!tip || !tecaj) return null;
        return new TerminiStore(api, tip, tecaj, limit);
    }, [api, tip, tecaj, limit]);

    useEffect(() => {
        return () => store?.dispose();
    }, [store]);

    if (!store) {
        return <EmptyMessage>Manjkajo parametri.</EmptyMessage>;
    }

    const buildHref = (lecture: calendar.IAvailableLecture) => {
        const date = new Date(lecture.dateTime!.ts as number);
        const ltd = `${date.getFullYear()}-${zfill(date.getMonth() + 1, 2)}-${zfill(date.getDate(), 2)}`;
        return CvvRoutes.prijavaNaTecaj(tip!, tecaj!, lecture.id!, ltd);
    };

    if (store.loading) {
        return (
            <PageContainer>
                <LoadingContainer>
                    <TailSpin color="#002D62" height={60} width={60}/>
                </LoadingContainer>
            </PageContainer>
        );
    }

    if (store.lectures.length === 0) {
        return (
            <EmptyStateWrapper>
                <EmptyContainer>
                    <EmptyIconContainer>
                        <CalendarIcon />
                    </EmptyIconContainer>
                    <EmptyTitle>
                        Ni razpisanih terminov
                    </EmptyTitle>
                    <EmptyText>
                        Trenutno za program nimamo razpisanih terminov. Vabljeni, da se vseeno prijavite, mi pa vas o naslednjem terminu obvestimo.
                    </EmptyText>
                    <ContactInfo>
                        Za več informacij nas pokličite na <a href="tel:080 26 36">080 26 36</a> ali pišite na <a href="mailto:cvv@amzs.si">cvv@amzs.si</a>.
                    </ContactInfo>
                    <SignUpButtonLink
                        href={CvvRoutes.prijavaNaTecaj(tip!, tecaj!)}
                        target="_top"
                        rel="noopener"
                    >
                        PRIJAVA BREZ TERMINA
                    </SignUpButtonLink>
                </EmptyContainer>
            </EmptyStateWrapper>
        );
    }

    return (
        <PageContainer>
            {store.lectures.map((lecture) => (
                <SlotWrapper key={lecture.id}>
                    <TerminiSlot
                        lecture={lecture}
                        href={buildHref(lecture)}
                    />
                </SlotWrapper>
            ))}
        </PageContainer>
    );
});
